<!--
  AIChatWidget.vue  — Live SEO Assistant (DeepSeek v4-flash)

  Backend:  POST /api/chat/message  { message, conversation_id?, page_context?, report_context? }
            → { reply, conversation_id }

  D-1c (2026-08-04): report_context is a lightweight summary (scores, AI-
  readiness, top recommendations) pushed by ReportPage.vue into the shared
  composables/useReportChatContext.js ref — this widget can't inject()
  reportData directly since it's mounted globally in App.vue, a sibling of
  <router-view>, not a descendant of ReportPage.vue.
-->
<template>
  <div>
    <!-- Launcher -->
    <button
      class="no-print fixed right-5 z-[900] w-14 h-14 rounded-full bg-ink-900 text-white shadow-float
             flex items-center justify-center transition-all duration-200
             hover:bg-ink-800 hover:-translate-y-0.5 hover:shadow-hero
             focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-500/30"
      :class="[isReportRoute ? 'bottom-36 xl:bottom-5' : 'bottom-5', { 'scale-0 opacity-0': open }]"
      @click="open = true"
      aria-label="Open AI assistant"
    >
      <span class="absolute inset-0 rounded-full bg-brand-500/30 animate-pulse-ring" aria-hidden="true"></span>
      <Icon name="sparkles" :size="22" />
    </button>

    <!-- Panel -->
    <Transition name="panel">
      <div
        v-if="open"
        class="no-print fixed right-5 z-[950] w-[calc(100vw-2.5rem)] sm:w-[380px] max-h-[70vh]
               card shadow-hero flex flex-col overflow-hidden"
        :class="isReportRoute ? 'bottom-36 xl:bottom-5' : 'bottom-5'"
      >
        <div class="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink-100 bg-white">
          <div class="flex items-center gap-2">
            <span class="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center">
              <Icon name="sparkles" :size="16" classes="text-white" />
            </span>
            <div>
              <div class="text-sm font-semibold text-ink-900 leading-tight">SEO Assistant</div>
              <div class="text-2xs text-ink-500 leading-tight flex items-center gap-1">
                <span class="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
                Ready to help
              </div>
            </div>
          </div>
          <button class="btn-ghost !p-1.5" @click="open = false" aria-label="Close">
            <Icon name="x" :size="16" />
          </button>
        </div>

        <div ref="scrollRef" class="flex-1 overflow-y-auto scroll-custom px-4 py-4 space-y-3 bg-ink-50/40">
          <div v-if="messages.length === 0" class="text-center py-6">
            <div class="w-12 h-12 mx-auto rounded-2xl bg-white border border-ink-200 flex items-center justify-center mb-3">
              <Icon name="sparkles" :size="22" classes="text-brand-600" />
            </div>
            <p class="text-sm text-ink-700 font-medium">How can I help today?</p>
            <p class="text-xs text-ink-500 mt-1">Ask about your SEO scores, fixes, or competitors.</p>
            <div class="mt-4 grid gap-2 text-left">
              <button v-for="s in (reportContext ? reportSuggestions : suggestions)" :key="s"
                class="text-xs text-ink-700 bg-white border border-ink-200 rounded-lg px-3 py-2 hover:border-ink-300 transition-colors"
                @click="input = s; send()">
                {{ s }}
              </button>
            </div>
          </div>

          <div v-for="(m, i) in messages" :key="i"
               :class="m.role === 'user' ? 'flex justify-end' : 'flex justify-start'">
            <!-- Monthly-quota-reached message (chat_endpoints.py's 402) gets
                 its own real CTA button rather than being folded into the
                 normal formatChatMessage/v-html pipeline — that pipeline
                 has no link support, and this text is app-controlled (not
                 DeepSeek output), so a plain, safe, dedicated block is
                 simpler and more reliable than teaching the formatter
                 markdown-link syntax for one call site. -->
            <div v-if="m.upgrade" class="max-w-[85%] rounded-2xl px-3.5 py-3 text-sm leading-relaxed bg-brand-50 border border-brand-100 text-ink-800 rounded-bl-sm shadow-soft">
              <p class="mb-2.5">{{ m.content }}</p>
              <router-link to="/pricing" class="btn-primary btn-sm inline-flex" @click="open = false">
                <Icon name="sparkles" :size="13" /> Upgrade to Pro
              </router-link>
            </div>
            <div v-else class="max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed"
                 :class="m.role === 'user'
                   ? 'bg-ink-900 text-white rounded-br-sm'
                   : 'bg-white border border-ink-100 text-ink-800 rounded-bl-sm shadow-soft'">
              <!-- Assistant replies get lightweight markdown-ish formatting
                   (paragraphs/bullets/bold) since the system prompt asks
                   DeepSeek for "2-4 paragraphs" but raw text interpolation
                   collapsed every newline into one dense blob. User's own
                   messages stay plain-text interpolation (never v-html) --
                   no reason to parse a user's own input as markdown, and
                   doing so would be an unnecessary XSS surface. -->
              <div v-if="m.role === 'assistant'" v-html="formatChatMessage(m.content)"></div>
              <template v-else>{{ m.content }}</template>
            </div>
          </div>

          <div v-if="typing" class="flex justify-start">
            <div class="bg-white border border-ink-100 rounded-2xl rounded-bl-sm px-3.5 py-2.5 shadow-soft">
              <span class="inline-flex gap-1">
                <span class="w-1.5 h-1.5 rounded-full bg-ink-400 animate-bounce" style="animation-delay:0ms"></span>
                <span class="w-1.5 h-1.5 rounded-full bg-ink-400 animate-bounce" style="animation-delay:120ms"></span>
                <span class="w-1.5 h-1.5 rounded-full bg-ink-400 animate-bounce" style="animation-delay:240ms"></span>
              </span>
            </div>
          </div>
        </div>

        <form @submit.prevent="send" class="border-t border-ink-100 p-3 bg-white">
          <div class="input-group">
            <input v-model="input" class="input" placeholder="Ask your SEO assistant…" :disabled="typing" />
            <button type="submit" class="btn-primary !rounded-none !rounded-r-lg" :disabled="!input.trim() || typing">
              <Icon name="arrow-right" :size="16" />
            </button>
          </div>
          <div class="text-2xs text-ink-500 mt-1.5 text-center">
            AI-powered SEO assistant
          </div>
        </form>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, nextTick, computed } from 'vue';
import { useRoute } from 'vue-router';
import Icon from './Icon.vue';
import { useReportChatContext } from '@/composables/useReportChatContext';
import { useProspectingChatContext } from '@/composables/useProspectingChatContext';

const route = useRoute();
const reportContext = useReportChatContext();
const prospectingContext = useProspectingChatContext();

// ReportPage.vue's mobile bottom nav bar (ReportNav.vue) and floating
// "current section" pill both sit fixed at the bottom on <xl screens — the
// launcher needs to clear them there instead of overlapping.
//
// Was 'report' only, with a comment claiming "neither exists on
// shared-report... routes" -- stale as of 2026-09-06: SharedReportPage.vue
// now renders <ReportNav> too (it didn't when that comment was written),
// so its mobile bottom bar creates the identical overlap there. Found
// while fixing the SEO Assistant's missing report_context on this same
// route -- not independently live-verified (ReportNav only mounts with
// real report data this environment can't authenticate/fetch), but the
// reasoning is direct: same component, same fixed-bottom CSS, same screens.
const isReportRoute = computed(() => route.name === 'report' || route.name === 'shared-report');

const open = ref(false);
const input = ref('');
const messages = ref([]);
const typing = ref(false);
const scrollRef = ref(null);
const conversationId = ref(null);
const error = ref('');

const suggestions = [
  'What does my SEO score mean?',
  'How do I improve Core Web Vitals?',
  'Suggest keywords for my site',
];

// Map route names to human-readable page context
const pageContext = computed(() => {
  const map = {
    home: 'Home',
    features: 'Features',
    pricing: 'Pricing',
    dashboard: 'Dashboard',
    analyze: 'Analyze',
    campaigns: 'Campaigns',
    'campaign-detail': 'Campaign Detail',
    settings: 'Settings',
    profile: 'Profile',
    learn: 'Learn / Blog',
    legal: 'Legal',
    prospecting: 'Prospecting',
    report: 'Report',
    // Real bug found live 2026-09-06: the shared-report route (its own
    // real route name, router/index.js -- distinct from 'report') was
    // absent from this map, so pageContext was null there. Combined with
    // the report_context gate below being hardcoded to route.name ===
    // 'report', the SEO Assistant had ZERO awareness it was even looking
    // at a report on shared links, let alone real report data.
    'shared-report': 'Shared Report',
  };
  return map[route.name] || null;
});

// D-1c: report-aware suggestion prompts when viewing a report — replaces
// the generic starters so the "quick start" buttons demonstrate the
// feature instead of asking something the widget can't yet answer well.
const reportSuggestions = [
  'Why is my AI Engine Readiness score low?',
  "What's the single highest-priority fix for my site?",
  'Explain my technical SEO score in plain language',
  // Owner ask 2026-09-07: users had no discoverable, guided path to
  // learn how to actually use Heading Optimizer's Strategic
  // Recommendations picker -- one click here surfaces the step-by-step
  // walkthrough now baked into chat_endpoints.py's SYSTEM_PROMPT.
  'How do I use the Heading Optimizer?',
];

const scrollToBottom = async () => {
  await nextTick();
  if (scrollRef.value) scrollRef.value.scrollTop = scrollRef.value.scrollHeight;
};

// Lightweight, dependency-free markdown-ish formatter for assistant
// replies — deliberately not a full markdown library (no new JS dep for
// this narrow a need, per the perf budget). Handles exactly what a DeepSeek
// SEO answer actually produces: paragraphs, **bold**, `code`, and -/1.
// lists. Escapes HTML FIRST, then injects only tags this function itself
// generates — raw model output can never execute as markup.
const escapeHtml = (s) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const formatInline = (s) =>
  escapeHtml(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+?)`/g, '<code class="bg-ink-100 text-ink-800 rounded px-1 py-0.5 text-2xs font-mono">$1</code>');

const formatChatMessage = (text) => {
  if (!text) return '';
  const blocks = text.trim().split(/\n{2,}/);
  return blocks
    .map((block) => {
      const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
      const isBulleted = lines.length > 0 && lines.every((l) => /^[-*]\s+/.test(l));
      const isNumbered = lines.length > 0 && lines.every((l) => /^\d+[.)]\s+/.test(l));
      if (isBulleted) {
        const items = lines.map((l) => `<li>${formatInline(l.replace(/^[-*]\s+/, ''))}</li>`).join('');
        return `<ul class="list-disc pl-4 mb-2 last:mb-0 space-y-0.5">${items}</ul>`;
      }
      if (isNumbered) {
        const items = lines.map((l) => `<li>${formatInline(l.replace(/^\d+[.)]\s+/, ''))}</li>`).join('');
        return `<ol class="list-decimal pl-4 mb-2 last:mb-0 space-y-0.5">${items}</ol>`;
      }
      return `<p class="mb-2 last:mb-0">${formatInline(block).replace(/\n/g, '<br>')}</p>`;
    })
    .join('');
};

const send = async () => {
  const text = input.value.trim();
  if (!text || typing.value) return;

  error.value = '';
  messages.value.push({ role: 'user', content: text });
  input.value = '';
  await scrollToBottom();
  typing.value = true;

  try {
    const body = { message: text };
    if (conversationId.value) body.conversation_id = conversationId.value;
    if (pageContext.value) body.page_context = pageContext.value;
    // Real bug found live 2026-09-06: hardcoded to 'report' only, so even
    // once SharedReportPage.vue started correctly populating reportContext,
    // it would never actually reach the backend on a shared link. Reuses
    // isReportRoute (already 'report' || 'shared-report') rather than a
    // second, independent route-name check that could drift from it.
    if (isReportRoute.value && reportContext.value) body.report_context = reportContext.value;
    if (route.name === 'prospecting' && prospectingContext.value) body.prospecting_context = prospectingContext.value;

    const res = await fetch('/api/chat/message', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (res.status === 429) {
      messages.value.push({
        role: 'assistant',
        content: "I'm getting a lot of questions right now. Give me a moment and try again.",
      });
      typing.value = false;
      await scrollToBottom();
      return;
    }

    // Starter tier's monthly SEO Assistant allowance is used up
    // (chat_endpoints.py's 402) — a real "upgrade" moment, not a generic
    // error, so it gets its own message shape + a real CTA button instead
    // of falling into the generic catch's "having trouble connecting" text
    // (which would be actively misleading here — nothing is broken).
    if (res.status === 402) {
      const data = await res.json().catch(() => ({}));
      messages.value.push({
        role: 'assistant',
        upgrade: true,
        content: data.detail || "You've used your free SEO Assistant messages this month.",
      });
      typing.value = false;
      await scrollToBottom();
      return;
    }

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || `Server error (${res.status})`);
    }

    const data = await res.json();
    conversationId.value = data.conversation_id;
    const reply = (data.reply || '').trim() ||
      "Hmm, I couldn't generate a response. This might be a temporary service issue, API credits exhausted, or a connection hiccup. Try refreshing or rephrasing — I'm here for SEO help: content, technical audits, keyword research, and competitor analysis. If it persists, reach out to LeadRanks support.";
    messages.value.push({ role: 'assistant', content: reply });
  } catch (e) {
    messages.value.push({
      role: 'assistant',
      content: "Sorry, I'm having trouble connecting. Please try again in a moment.",
    });
    error.value = e.message;
  }

  typing.value = false;
  await scrollToBottom();
};
</script>

<style scoped>
.panel-enter-active, .panel-leave-active {
  transition: transform 220ms cubic-bezier(.22,.61,.36,1), opacity 220ms ease;
}
.panel-enter-from, .panel-leave-to {
  opacity: 0; transform: translateY(12px) scale(.98);
}
</style>
